import { useCallback } from 'react';

import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';

import { type CTAScenario } from './useCTAScenario';
import { useDuplicateHandler } from './useDuplicateHandler';
import { useDuplicateLockedHandler } from './useDuplicateLockedHandler';
import { useEditHandler } from './useEditHandler';
import { useEnterHandler } from './useEnterHandler';
import { useExportWorksheetHandler } from './useExportWorksheetHandler';
import { useFlashcardHandler } from './useFlashcardHandler';
import { useGoToLmsHandler } from './useGoToLmsHandler';
import { useLinkToClassroomHandler } from './useLinkToClassroomHandler';
import { useShareHandler } from './useShareHandler';
import { useUpgradePlanHandler } from './useUpgradePlanHandler';

export interface CTAHandlers {
  handleEnter: () => void;
  handleShare: () => void;
  handleDuplicate: () => void;
  handleEdit: () => void;
  handleGoToLms: () => void;
  handleDuplicateLocked: () => void;
  handleUpgradePlan: () => void;
  handleExportWorksheet: () => void;
  handleFlashcard: () => void;
  handleLinkToClassroom: () => void;
}

interface UseCTAHandlersParams {
  quizId: string;
  quizData: QuizSetDetailsRes;
  scenario: CTAScenario;
}

/**
 * useCTAHandlers - CTA 버튼 핸들러 통합 훅
 *
 * @description
 * 퀴즈 상세 페이지의 모든 CTA 버튼 핸들러를 제공하는 통합 훅입니다.
 * 각 핸들러는 독립적인 훅으로 분리되어 있으며, 이 훅은 이들을 조합하여 반환합니다.
 *
 * 개별 핸들러:
 * - useEnterHandler: 퀴즈 입장
 * - useShareHandler: 공유하기
 * - useDuplicateHandler: 복사하기
 * - useEditHandler: 수정하기
 * - useGoToLmsHandler: 학습 리포트
 * - useDuplicateLockedHandler: 잠긴 퀴즈 복사
 * - useUpgradePlanHandler: 플랜 업그레이드
 */
export const useCTAHandlers = ({
  quizId,
  quizData,
  scenario,
}: UseCTAHandlersParams): CTAHandlers => {
  const handleEnter = useEnterHandler({ quizData, scenario });
  const handleShare = useShareHandler({ quizData });
  const handleDuplicate = useDuplicateHandler({ quizId, quizData });
  const handleEdit = useEditHandler({ quizId, quizData });
  const handleGoToLms = useGoToLmsHandler({ quizId });
  const handleDuplicateLocked = useDuplicateLockedHandler({ quizId, quizData });
  const handleUpgradePlan = useUpgradePlanHandler();
  const handleExportWorksheet = useExportWorksheetHandler({ quizData });
  const handleFlashcard = useFlashcardHandler({ quizId, quizData });
  const linkToClassroom = useLinkToClassroomHandler({ quizData });
  const handleLinkToClassroom = useCallback(() => {
    void linkToClassroom();
  }, [linkToClassroom]);

  return {
    handleEnter,
    handleShare,
    handleDuplicate,
    handleEdit,
    handleGoToLms,
    handleDuplicateLocked,
    handleUpgradePlan,
    handleExportWorksheet,
    handleFlashcard,
    handleLinkToClassroom,
  };
};
